import { AddressTypeEnum, PromotionCalculationTypeEnum } from "@lib/enum"
import { ignoreBodyByModel } from "@models/global"
import { toNumberOrZero } from "@utils/toNumberOrZero"
import dayjs from "dayjs"

/** Resolve CTKM for contract-event forms (create/edit); avoids empty-array || fallback trap. */
export function resolveContractPromotionPrograms(detail?: any, contract?: any): any[] {
  const c = contract ?? detail?.contract ?? detail
  if (detail?.contractEventPromotionPrograms?.length) {
    return detail.contractEventPromotionPrograms
  }
  if (detail?.promotionPrograms?.length) {
    return detail.promotionPrograms
  }
  return c?.promotionPrograms ?? []
}

/**
 * Chỉ id cấu hình thanh toán THẬT (> 0) mới dùng được.
 * Hợp đồng import/migrate có `configurationPaymentId` = 0 hoặc thiếu hẳn; gửi giá trị đó xuống
 * BE thì `ContractPaymentCalculatorCore.CalculateAsync` (ContractPaymentCalculatorCore.cs:76-79)
 * ném `EntityNotFoundException(ConfigurationPayment, 0)` -> lỗi "There is no entity
 * ConfigurationPayment with id = 0!".
 */
export function isValidConfigurationPaymentId(id: unknown): id is number {
  return typeof id === "number" && id > 0
}

/** Lấy id cấu hình thanh toán hợp lệ đầu tiên trong danh sách nguồn ưu tiên. */
export function resolveConfigurationPaymentId(
  ...sources: unknown[]
): number | undefined {
  for (const source of sources) {
    if (isValidConfigurationPaymentId(source)) return source
  }
  return undefined
}

/** Migrated/imported contracts may have calculationMethod 0 — default to CASCADING. */
export function resolveEventCalculationMethod(
  paymentSchedule?: any,
  contract?: any,
  detail?: any
): PromotionCalculationTypeEnum {
  const raw =
    detail?.updateCalculationMethod ??
    detail?.calculationMethod ??
    paymentSchedule?.calculationMethod ??
    contract?.calculationMethod
  if (
    raw === PromotionCalculationTypeEnum.CASCADING ||
    raw === PromotionCalculationTypeEnum.ADDITIVE
  ) {
    return raw
  }
  return PromotionCalculationTypeEnum.CASCADING
}

export class ContractModel {
  id: any
  constructor() {
    this.id = undefined
  }

  public static assign(obj) {
    if (!obj) return undefined

    const newObj = Object.assign(new ContractModel(), obj)

    newObj.validDate = obj.validDate ? dayjs(obj.validDate) : null
    newObj.expiredDate = obj.expiredDate ? dayjs(obj.expiredDate) : null
    newObj.signedDate = obj.signedDate ? dayjs(obj.signedDate) : null
    // newObj.contractCustomers = CustomerModel.assigns(obj.contractCustomers)

    newObj.contractCustomers = CustomerContractModel.assigns(
      obj.contractCustomers
    )
    newObj.promotionPrograms = ContractEventPromotionProgramModel.assigns(
      obj.promotionPrograms || []
    )
    return newObj
  }

  public static assigns(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}

export class CustomerContractEventModel {
  existingCustomerId: any = undefined
  isReceiveNotification?: boolean = false
  isInContract?: boolean = false
  dataJson?: string = undefined
  public static assign(obj) {
    if (!obj) return undefined
    const newObj = Object.assign(new CustomerContractEventModel(), obj)
    const rawExistingId = obj.existingCustomerId
    const hasExistingCustomerId =
      rawExistingId != null &&
      rawExistingId !== 0 &&
      rawExistingId !== "0" &&
      rawExistingId !== ""
    // KH mới: chỉ `dataJson`, KHÔNG fallback `obj.id` (id hiển thị trên form) → tránh gán nhầm KHHH.
    newObj.existingCustomerId = hasExistingCustomerId ? rawExistingId : undefined
    newObj.isReceiveNotification = obj.isReceiveNotification || false
    newObj.isInContract = obj.isInContract || false
    newObj.dataJson = obj.dataJson
    return ignoreBodyByModel(newObj, CustomerContractEventModel)
  }
  public static assigns(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}
export class ContractEventModel {
  id: any
  contractEventPromotionPrograms?: ContractEventPromotionProgramModel[]
  constructor() {
    this.id = undefined
  }

  public static assign(obj) {
    if (!obj) return undefined

    const newObj = Object.assign(new ContractEventModel(), obj)

    newObj.transferDate = obj.transferDate ? dayjs(obj.transferDate) : null
    newObj.eventDate = obj.eventDate ? dayjs(obj.eventDate) : null
    newObj.spaDate = obj.spaDate ? dayjs(obj.spaDate) : null
    newObj.actualSPADate = obj.actualSPADate ? dayjs(obj.actualSPADate) : null
    newObj.ltlDate = obj.ltlDate ? dayjs(obj.ltlDate) : null
    newObj.actualLTLDate = obj.actualLTLDate ? dayjs(obj.actualLTLDate) : null

    newObj.contractEventPromotionPrograms = ContractEventPromotionProgramModel.assigns(
      resolveContractPromotionPrograms(obj, obj.contract)
    )

    return newObj
  }

  public static assigns(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}
export class ContractEventPromotionProgramModel {
  id?: number
  promotionProgramId?: number
  description?: string
  percentage?: number
  amount?: number
  type?: number
  name?: string
  isSpecial?: boolean
  discount?: number
  priceAfter?: number

  public static assigns(objs: any[]) {
    return (objs || []).map((obj) => {
      const newObj = new ContractEventPromotionProgramModel()
      newObj.id = obj.id
      newObj.promotionProgramId =
        obj.promotionProgramId ?? obj.promotionProgram?.id ?? undefined
      newObj.description = obj.description
      newObj.percentage = obj.percentage
      newObj.amount = obj.amount
      newObj.type = obj.type || 1
      newObj.name = obj.name
      newObj.isSpecial = obj.isSpecial || false
      newObj.discount = obj.discount
      newObj.priceAfter = obj.priceAfter
      return newObj
    })
  }
}

export class DA2SPAUpdateModel {
  id?: number
  contractId?: number
  eventDate?: string
  spaCode?: string
  spaDate?: string
  actualSPADate?: string
  description?: string
  isDraft?: boolean
  contractTypeId?: number
  unitAmount?: number
  totalDiscountAmount?: number
  contractAmount?: number
  updateAmountAfterDiscounted?: number
  updateDiscountAmount?: number
  updateSpecialDiscountAmount?: number
  updateMaintenanceAmount?: number
  updateMaintenanceFeePercent?: number
  updateVatAmount?: number
  updateTotalAmount?: number
  updateDiscountAmountPercent?: number
  updateVatPercent?: number
  updateCalculationMethod?: number
  /** Feature-0380 — chỉ sự kiện UpdateSalesPolicy gửi; các loại khác để undefined. */
  updateSalesPolicyId?: number
  lTLcode?: string
  ltlDate?: string
  actualLTLDate?: string
  contractPaymentSchedule?: {
    validDate?: string
    spaOrLTLDate?: string
    daDate?: string
    configurationPaymentId?: number
    totalDiscountAmount?: number
    vatTax?: number
    unitId?: number
    maintenancePercentage?: number
  }
  customers?: any[]
  contractEventPromotionPrograms?: ContractEventPromotionProgramModel[]
  promotionPrograms?: ContractEventPromotionProgramModel[]

  public static assign(obj: any) {
    if (!obj) return undefined
    const newObj = new DA2SPAUpdateModel()

    newObj.id = obj.id
    newObj.contractId = obj.contractId
    newObj.eventDate = obj.eventDate && dayjs(obj.eventDate).isValid() ? dayjs(obj.eventDate).toISOString() : obj.eventDate
    newObj.spaCode = obj.spaCode
    newObj.spaDate = obj.spaDate && dayjs(obj.spaDate).isValid() ? dayjs(obj.spaDate).toISOString() : obj.spaDate
    newObj.lTLcode = obj.lTLcode
    newObj.ltlDate = obj.ltlDate && dayjs(obj.ltlDate).isValid() ? dayjs(obj.ltlDate).toISOString() : obj.ltlDate
    newObj.actualLTLDate = obj.actualLTLDate && dayjs(obj.actualLTLDate).isValid() ? dayjs(obj.actualLTLDate).toISOString() : obj.actualLTLDate
    newObj.actualSPADate = obj.actualSPADate && dayjs(obj.actualSPADate).isValid() ? dayjs(obj.actualSPADate).toISOString() : obj.actualSPADate
    newObj.description = obj.description
    newObj.isDraft = obj.isDraft
    newObj.contractTypeId = obj.contractTypeId
    newObj.unitAmount = obj.unitAmount
    newObj.totalDiscountAmount = obj.totalDiscountAmount
    newObj.contractAmount = obj.contractAmount
    newObj.updateAmountAfterDiscounted = obj.updateAmountAfterDiscounted || obj.amountAfterDiscounted
    newObj.updateDiscountAmount = obj.updateDiscountAmount || obj.discountAmount
    newObj.updateSpecialDiscountAmount = obj.updateSpecialDiscountAmount || obj.specialDiscountAmount
    newObj.updateMaintenanceAmount = obj.updateMaintenanceAmount || obj.maintenanceAmount
    // % Phí bảo trì user nhập nằm ở field `maintenancePercentage` của form; thiếu key này trong
    // whitelist thì BE nhận null -> nhánh create fallback về % của HĐ gốc, nhánh update ghi null,
    // nên sửa 2 -> 3 lưu nháp xong mở lại vẫn thấy 2. Ô nhập là nguồn duy nhất của form này:
    // xoá trắng (null) hiểu là 0% chứ không phải "không đổi"; chỉ khi form không có field mới
    // giữ giá trị cũ của sự kiện.
    newObj.updateMaintenanceFeePercent =
      obj.maintenancePercentage !== undefined
        ? toNumberOrZero(obj.maintenancePercentage)
        : obj.updateMaintenanceFeePercent
    newObj.updateVatAmount = obj.updateVatAmount || obj.vatAmount
    newObj.updateTotalAmount = obj.updateTotalAmount || obj.totalAmount
    newObj.updateDiscountAmountPercent = obj.updateDiscountAmountPercent || obj.discountAmountPercent
    newObj.updateVatPercent = obj.updateVatPercent ?? obj.vatTax
    newObj.updateCalculationMethod = obj.updateCalculationMethod ?? obj.calculationMethod
    // 0 / "" nghĩa là "chưa chọn" chứ không phải id nhỏ -> bỏ hẳn key thay vì gửi 0.
    newObj.updateSalesPolicyId = obj.updateSalesPolicyId || undefined

    if (obj.contractPaymentSchedule) {
      newObj.contractPaymentSchedule = {
        validDate: obj.contractPaymentSchedule.validDate && dayjs(obj.contractPaymentSchedule.validDate).isValid() ? dayjs(obj.contractPaymentSchedule.validDate).toISOString() : obj.contractPaymentSchedule.validDate,
        spaOrLTLDate: obj.contractPaymentSchedule.spaOrLTLDate && dayjs(obj.contractPaymentSchedule.spaOrLTLDate).isValid() ? dayjs(obj.contractPaymentSchedule.spaOrLTLDate).toISOString() : obj.contractPaymentSchedule.spaOrLTLDate,
        daDate: obj.contractPaymentSchedule.daDate && dayjs(obj.contractPaymentSchedule.daDate).isValid() ? dayjs(obj.contractPaymentSchedule.daDate).toISOString() : obj.contractPaymentSchedule.daDate,
        configurationPaymentId: obj.contractPaymentSchedule.configurationPaymentId,
        totalDiscountAmount: obj.contractPaymentSchedule.totalDiscountAmount || obj.contractPaymentSchedule.amountAfterDiscounted,
        vatTax: obj.contractPaymentSchedule.vatTax,
        unitId: obj.contractPaymentSchedule.unitId,
        // Whitelist này quyết định payload gửi lên; thiếu maintenancePercentage thì BE bind về 0,
        // không sinh kỳ Phí bảo trì -> % về 0 và mất dòng công nợ Phí bảo trì sau khi lưu nháp.
        // Xoá trắng ô % -> AntD trả null, còn CalculatePaymentDto.MaintenancePercentage là decimal
        // KHÔNG nullable => BE ném AbpValidationException, lưu nháp fail. Xoá % hiểu là 0.
        maintenancePercentage: toNumberOrZero(
          obj.contractPaymentSchedule.maintenancePercentage
        ),
      }
    }

    newObj.customers = CustomerContractEventModel.assigns(obj.customers)

    const mappedPromotions = ContractEventPromotionProgramModel.assigns(
      resolveContractPromotionPrograms(obj, obj.contract)
    )
    newObj.contractEventPromotionPrograms = mappedPromotions
    newObj.promotionPrograms = mappedPromotions

    return newObj
  }
}

export class ContractEventUpdateModel {
  id: any
  contractId?: any
  eventDate?: any
  calculateDate?: any
  description?: string
  isDraft?: boolean
  unitAmount?: number
  totalDiscountAmount?: number
  contractAmount?: number
  totalInterestReductionAmount?: number
  updateVatPercent?: number
  updateCalculationMethod?: number
  customers?: any[]
  customerHistories?: any[] // Field cho DATransfer event
  contractEventInterestReductions?: any[]
  type?: number
  requestId?: any

  constructor() {
    this.id = undefined
  }

  public static assign(obj) {
    if (!obj) return undefined

    switch (obj.type) {
      case 2: // DA2SPA
        return DA2SPAUpdateModel.assign(obj)
      case 3: // DA2LTL
        return DA2SPAUpdateModel.assign(obj)
      // Feature-0380 — UpdateSalesPolicy dùng CHUNG whitelist của DA2SPA: cùng 6 cột tiền, cùng
      // `contractPaymentSchedule`, cùng cách chuẩn hoá ngày + maintenancePercentage. 3 ô SPA của
      // model là undefined nên bị JSON.stringify bỏ, BE cũng không có property tương ứng.
      case 16: // UpdateSalesPolicy
        return DA2SPAUpdateModel.assign(obj)
      case 4: // DATransfer - Loại bỏ contractCustomers, chỉ gửi customerHistories
        return this.assignDATransfer(obj)
      default:
        return this.assignDefault(obj)
    }
  }

  private static assignDATransfer(obj) {
    // IMPORTANT: Loại bỏ contract và contractCustomers từ obj (chỉ áp dụng cho DATransfer)
    // Chỉ giữ lại customerHistories, không gửi contractCustomers lên API
    
    // Loại bỏ contract và contractCustomers bằng destructuring
    const { 
      contract, 
      contractCustomers, 
      ...objWithoutContract 
    } = obj
    
    // Tạo newObj và chỉ copy các field được phép (không copy contractCustomers)
    const newObj = new ContractEventUpdateModel()
    
    // Copy các field được định nghĩa trong ContractEventUpdateModel từ objWithoutContract
    // Nhưng loại bỏ contractCustomers nếu có (do destructuring có thể không hoạt động với nested)
    Object.keys(objWithoutContract).forEach(key => {
      // Bỏ qua contractCustomers và contract (nếu vẫn còn)
      if (key !== 'contractCustomers' && key !== 'contract') {
        newObj[key] = objWithoutContract[key]
      }
    })
    
    // Xử lý customers
    newObj.customers = CustomerContractEventModel.assigns(obj.customers)
    
    // Đảm bảo customerHistories được giữ lại từ obj (nếu có)
    if (obj.customerHistories) {
      newObj.customerHistories = obj.customerHistories
    }

    // Set các field cơ bản từ obj (nếu có)
    if (obj.id !== undefined) newObj.id = obj.id
    if (obj.contractId !== undefined) newObj.contractId = obj.contractId
    if (obj.eventDate !== undefined) newObj.eventDate = obj.eventDate
    if (obj.calculateDate !== undefined) newObj.calculateDate = obj.calculateDate
    if (obj.description !== undefined) newObj.description = obj.description
    if (obj.isDraft !== undefined) newObj.isDraft = obj.isDraft
    if (obj.type !== undefined) newObj.type = obj.type
    if (obj.requestId !== undefined) newObj.requestId = obj.requestId

    // Xử lý date fields
    const dateFields = ["eventDate", "calculateDate"]
    dateFields.forEach(field => {
      if (newObj[field] && dayjs.isDayjs(newObj[field])) {
        newObj[field] = newObj[field].toISOString()
      }
    })

    // Đảm bảo không có contractCustomers trong payload cuối cùng (triple check)
    if ('contractCustomers' in newObj) {
      delete (newObj as any).contractCustomers
    }
    
    // Đảm bảo không có contract object trong payload
    if ('contract' in newObj) {
      delete (newObj as any).contract
    }
    
    // Log để debug

    return newObj
  }

  private static assignDefault(obj) {
    const { contract, ...objWithoutContract } = obj
    const newObj = Object.assign(new ContractEventUpdateModel(), objWithoutContract)
    newObj.customers = CustomerContractEventModel.assigns(obj.customers)

    const dateFields = ["eventDate", "calculateDate"]
    dateFields.forEach(field => {
      if (newObj[field] && dayjs.isDayjs(newObj[field])) {
        newObj[field] = newObj[field].toISOString()
      }
    })

    return newObj
  }
}
export class ContractUpdateModel {
  id: any
  constructor() {
    this.id = undefined
  }

  public static assign(obj) {
    if (!obj) return undefined

    const newObj = Object.assign(new ContractUpdateModel(), obj)

    newObj.contractCustomers = CustomerContractUpdateModel.assigns(
      obj.contractCustomers
    )
    return newObj
  }

  public static assigns(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}

export class BookingToContractModel {
  isBooked?: boolean = false
  isQuota?: boolean = false
  bookingId?: number = undefined
  quotaValue?: number = undefined
  bookingApprovalDate?: string = undefined
  booking?: any = undefined
  contractCustomers?: CustomerContractModel[] = []
  agencyMasterId?: number = undefined
  staffId?: number = undefined
  roiDate?: string = undefined
  bookingDate?: string = undefined
  unitId?: number = undefined
  unit?: any = undefined
  cartId?: number = undefined
  projectId?: number = undefined
  depositAmount?: number = undefined
  salesPolicyId?: number = undefined
  promotionProgramIds?: number[] = []
  configurationPaymentId?: number = undefined
  constructor() { }
  public static assign(obj) {
    if (!obj) return undefined

    const newObj = Object.assign(new BookingToContractModel(), obj)

    newObj.isBooked = obj.isForeigner
    // Cờ quota lấy theo booking (BE trả `isQuota`), không suy ra từ khách nước ngoài;
    // booking không dùng quota phải để trống % quota trên form tạo HĐ.
    newObj.isQuota = !!obj.isQuota
    newObj.quotaValue = obj.isQuota ? obj.quotaPercent : undefined
    newObj.booking = {
      id: obj.id,
      code: obj.code,
    }
    newObj.bookingId = obj.id
    newObj.bookingDate = obj.bookingDate
      ? dayjs(obj.bookingDate).toISOString()
      : null
    newObj.roiDate = obj.bookingDate
      ? dayjs(obj.bookingDate).toISOString()
      : null
    newObj.depositAmount = obj.depositAmountForBooking
    newObj.contractCustomers = CustomerContractModel.assigns(
      obj.customers,
      true
    )
    newObj.configurationPaymentId = obj.configurationPaymentIds
      ? obj.configurationPaymentIds[0]
      : undefined
    // Đảm bảo unit object được copy để hiển thị tên unit khi tạo contract từ deposit
    if (obj.unit) {
      newObj.unit = obj.unit
    }
    return ignoreBodyByModel(newObj, BookingToContractModel)
  }

  public static assigns(objs) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item)))
    return results
  }
}

export class CustomerContractUpdateModel {
  existingCustomerId?: number = undefined
  firstName?: string = ""
  lastName?: string = ""
  fullName?: string = ""
  birthDate?: string = ""
  gender?: number = undefined
  customerPhoneNumbers?: any = []
  customerEmailAddresses?: any = []
  customerAddresses?: any = []
  description?: string = ""
  isReceiveNotification?: boolean = false
  isReceiveEmail?: boolean = false
  isReceiveSms?: boolean = false
  isReceiveCall?: boolean = false
  identifierCardNumber?: string = ""
  idIssuedDate?: string = ""
  idIssuedPlace?: string = ""
  nationalityId?: number = undefined
  contactPersonName?: string = ""
  contactPersonIdNumber?: string = ""
  contactPersonPhoneNumbers?: string[] = []
  contactPersonEmails?: string[] = []
  contactNote?: string = ""
  isContact?: boolean = false
  isCompany?: boolean = false
  isInContract?: boolean = false
  commonlyUsedBankName?: string = ""
  bankNumber?: string = ""
  bankBranch?: string = ""
  bankAddress?: string = ""
  beneficiary?: string = ""
  bankName?: string = ""
  bankSwiftCode?: string = ""
  companyName?: string = ""
  companyTaxCode?: string = ""
  companyAddress?: string = ""
  shortName?: string = ""
  hasCurrentAddress?: boolean = false
  customerType?: any = undefined
  legalRepresentative?: string = ""
  position?: string = ""
  legalRepresentativeIdentityCardNumber?: string = ""
  legalRepresentativeIdIssueDate?: string = ""
  legalRepresentativeIdIssuePlace?: string = ""
  constructor() { }
  public static assign(obj, isBooking?: boolean) {
    if (!obj) return undefined

    const newObj = Object.assign(new CustomerContractUpdateModel(), obj)
    newObj.customerAddresses = [
      obj.currentAddress
        ? { addressType: AddressTypeEnum.CURRENT, ...obj.currentAddress }
        : undefined,
      obj.permanentAddress
        ? { addressType: AddressTypeEnum.PERMANENT, ...obj.permanentAddress }
        : undefined,
    ].filter((item) => item !== undefined)

    return ignoreBodyByModel(newObj, CustomerContractUpdateModel)
  }

  public static assigns(objs, isBooking?: boolean) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item, isBooking)))
    return results
  }
}
export class CustomerContractModel {
  existingCustomerId?: number = undefined
  existingCustomerCode?: number = undefined
  id?: number = undefined
  firstName?: string = ""
  lastName?: string = ""
  fullName?: string = ""
  birthDate?: string = ""
  gender?: number = undefined
  customerPhoneNumbers?: any = []
  customerEmailAddresses?: any = []
  customerAddresses?: any = []
  description?: string = ""
  identifierCardNumber?: string = ""
  idIssuedDate?: string = ""
  idIssuedPlace?: string = ""
  nationalityId?: number = undefined
  contactPersonName?: string = ""
  contactPersonIdNumber?: string = ""
  contactPersonPhoneNumbers?: string[] = []
  contactPersonEmails?: string[] = []
  contactNote?: string = ""
  isContact?: boolean = false
  isCompany?: boolean = false
  isInContract?: boolean = false
  primaryPhone?: boolean = false
  primaryEmail?: boolean = false
  companyName?: string = ""
  companyTaxCode?: string = ""
  companyAddress?: string = ""
  currentAddress?: any = undefined
  permanentAddress?: any = undefined
  shortName?: string = ""
  //
  hasCurrentAddress?: boolean = false
  customerType?: any = undefined
  legalRepresentative?: any = undefined
  legalRepresentativeIdentityCardNumber?: string = ""
  legalRepresentativeIdIssueDate?: string = ""
  legalRepresentativeIdIssuePlace?: string = ""
  position?: any = undefined

  beneficiary?: any = undefined
  bankNumber?: any = undefined
  bankName?: any = undefined
  bankBranch?: any = undefined

  constructor() { }
  public static assign(obj, isBooking?: boolean) {
    if (!obj) return undefined
    const newObj = Object.assign(new CustomerContractModel(), obj)
    if (isBooking) {
      newObj.existingCustomerId = null
      newObj.isContact = obj.isRepresen
      newObj.contactPersonName = obj.represenFullName
      newObj.contactPersonIdNumber = obj.represenIdentifierCardNumber
      newObj.contactPersonPhoneNumbers = obj.represenPhoneNumbers
      newObj.contactPersonEmails = obj.represenEmails
      newObj.contactNote = obj.represenDescription

      // Lấy các field từ nested customer object nếu không có trong booking customer
      if (obj.customer) {
        // Lấy customerType từ customer nếu không có trong booking customer
        if (!newObj.customerType && obj.customer.customerType !== undefined) {
          newObj.customerType = obj.customer.customerType
        }

        // Lấy customerPhoneNumbers từ customer nếu booking customer không có hoặc là empty array
        if ((!newObj.customerPhoneNumbers || (Array.isArray(newObj.customerPhoneNumbers) && newObj.customerPhoneNumbers.length === 0))
          && obj.customer.customerPhoneNumbers && Array.isArray(obj.customer.customerPhoneNumbers) && obj.customer.customerPhoneNumbers.length > 0) {
          newObj.customerPhoneNumbers = obj.customer.customerPhoneNumbers
        }

        // Lấy customerEmailAddresses từ customer nếu booking customer không có hoặc là empty array
        if ((!newObj.customerEmailAddresses || (Array.isArray(newObj.customerEmailAddresses) && newObj.customerEmailAddresses.length === 0))
          && obj.customer.customerEmailAddresses && Array.isArray(obj.customer.customerEmailAddresses) && obj.customer.customerEmailAddresses.length > 0) {
          newObj.customerEmailAddresses = obj.customer.customerEmailAddresses
        }

        // Lấy banking fields từ customer nếu booking customer không có
        if (!newObj.bankName && obj.customer.bankName) {
          newObj.bankName = obj.customer.bankName
        }
        if (!newObj.bankNumber && obj.customer.bankNumber) {
          newObj.bankNumber = obj.customer.bankNumber
        }
        if (!newObj.bankBranch && obj.customer.bankBranch) {
          newObj.bankBranch = obj.customer.bankBranch
        }
        if (!newObj.beneficiary && obj.customer.beneficiary) {
          newObj.beneficiary = obj.customer.beneficiary
        }
        if (!newObj.description && obj.customer.description) {
          newObj.description = obj.customer.description
        }
        // Lấy commonlyUsedBankName nếu bankName không có
        if (!newObj.bankName && obj.customer.commonlyUsedBankName) {
          newObj.bankName = obj.customer.commonlyUsedBankName
        }
      }
    } else {
      newObj.currentAddress = obj.customerAddresses?.find(
        (item) => item.addressType === AddressTypeEnum.CURRENT
      )
      newObj.permanentAddress = obj.customerAddresses?.find(
        (item) => item.addressType === AddressTypeEnum.PERMANENT
      )
    }
    newObj.existingCustomerCode = obj.existingCustomer?.code ?? null

    newObj.hasCurrentAddress = !!newObj.currentAddress

    return ignoreBodyByModel(newObj, CustomerContractModel)
  }

  public static assigns(objs, isBooking?: boolean) {
    const results: any[] = []
    objs?.forEach((item) => results.push(this.assign(item, isBooking)))
    return results
  }
}

/**
 * Một dòng xem trước của tệp import AR Generate
 * (`POST api/Imports/Contract/ValidateBeforeImport`).
 *
 * Ba field LUF ở cuối tương ứng cột 15/16/17 của tệp và nhập tay được — khi có số thì
 * file thắng, BE bỏ `RecalculateAsync` cho toàn bộ hợp đồng đó.
 * Lưu ý: BE khai `decimal` không nullable nên ô trống trả về `0`, KHÔNG phải `null`;
 * không dùng giá trị xem trước để suy ra hợp đồng có đang ở chế độ file-thắng hay không.
 * Hợp đồng FE↔BE: `UI/Portal/docs/ar-generate-import-land-use-fee-fe-integration.md`.
 */
export interface IARGenerateImportRow {
  code: string
  unitName: string
  phaseCode: string
  feeTypeCode: string
  percentPayment: number
  amount: number
  vatAmount: number
  dueDate: string
  isHaveLandUseFee: string
  isHandoverPeriod: string
  isHaveMaintenanceFee: string
  isHaveManagementFee: string
  isUsed: string
  isHavePinkBookVatFee: string
  vatAmountBeforeLUF: number
  landUseFeeVatAmount: number
  landUseFeeAmount: number
}
